if (!customElements.get('er-product')) {
  customElements.define('er-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
      this.form = this.querySelector('.er-product__form');
      this.select = this.querySelector('[data-variant-select]');
      this.radios = [...this.querySelectorAll('[data-option-radio]')];
      this.fields = [...this.querySelectorAll('[data-option-index]')];
      this.variant = this.config.variants.find(variant => variant.id === this.config.selectedId);
      this.controller = new AbortController();
      this.mobileQuery = window.matchMedia('(max-width: 989px)');
      this.initializeOptionSelection();
      this.cartEventsPromise = import('@theme/events').catch(() => null);
      const events = { signal: this.controller.signal };
      const enhancedOptions = this.querySelector('[data-enhanced-options]');
      if (enhancedOptions) {
        enhancedOptions.hidden = false;
        this.querySelector('[data-variant-fallback]').hidden = true;
      }
      this.radios.forEach(radio => {
        // Clicking the already checked default is also an intentional choice.
        radio.addEventListener('click', () => this.confirmOption(radio), events);
        radio.addEventListener('change', () => this.selectVariant(radio), events);
      });
      this.select.addEventListener('change', () => {
        this.variant = this.config.variants.find(variant => String(variant.id) === this.select.value);
        this.syncRadios();
        if (this.variant) this.confirmAllOptions();
        this.renderVariant();
      }, events);
      this.querySelectorAll('[data-thumb-id]').forEach(button => button.addEventListener('click', () => this.showImage(button.dataset.thumbId), events));
      this.querySelectorAll('[data-close-dialog]').forEach(button => button.addEventListener('click', () => button.closest('dialog').close(), events));
      this.querySelectorAll('[data-zoom-dialog]').forEach(dialog => dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, events));
      this.querySelectorAll('[data-zoom-source], [data-lookbook-image]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        const mediaId = link.dataset.mediaId || link.dataset.lookbookImage;
        if (link.hidden || !this.visibleMediaIds?.has(String(mediaId))) return;
        const image = document.createElement('img');
        image.src = link.dataset.zoomSource || link.href;
        image.alt = link.querySelector('img').alt;
        this.querySelector('[data-zoom-image-container]').replaceChildren(image);
        this.querySelector('[data-zoom-dialog]').showModal();
      }, events));
      this.form.addEventListener('submit', event => this.addToCart(event), events);
      const stage = this.querySelector('[data-gallery-stage]');
      let touchStart = null;
      stage.addEventListener('touchstart', event => {
        touchStart = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }, { ...events, passive: true });
      stage.addEventListener('touchend', event => {
        if (!touchStart) return;
        const dx = event.changedTouches[0].clientX - touchStart.x;
        const dy = event.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
        const slides = [...this.querySelectorAll('[data-media-id]')].filter(slide => this.visibleMediaIds?.has(slide.dataset.mediaId));
        if (slides.length < 2) return;
        const current = slides.findIndex(slide => !slide.hidden);
        const next = (current + (dx < 0 ? 1 : -1) + slides.length) % slides.length;
        this.showImage(slides[next].dataset.mediaId);
      }, { ...events, passive: true });
      this.mainButton = this.querySelector('[data-main-add]');
      const updateSticky = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.updateStickyVisibility();
        });
      };
      document.addEventListener('scroll', updateSticky, { ...events, passive: true, capture: true });
      window.addEventListener('resize', updateSticky, { ...events, passive: true });
      this.mobileQuery.addEventListener('change', updateSticky, events);
      ['toggle', 'close', 'dialog:open', 'dialog:close'].forEach(name => {
        document.addEventListener(name, updateSticky, { ...events, capture: true });
      });
      if ('MutationObserver' in window) {
        this.overlayObserver = new MutationObserver(updateSticky);
        this.overlayObserver.observe(document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal']
        });
      }
      if ('ResizeObserver' in window) {
        this.stickyObserver = new ResizeObserver(updateSticky);
        this.stickyObserver.observe(this);
      }
      updateSticky();
      window.addEventListener('popstate', () => {
        const variantId = new URL(window.location.href).searchParams.get('variant');
        const variant = this.config.variants.find(item => String(item.id) === variantId);
        if (!variant) return;
        this.variant = variant;
        this.syncRadios();
        this.confirmAllOptions();
        this.renderVariant(false);
      }, events);
      this.renderVariant(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.stickyObserver?.disconnect();
      this.overlayObserver?.disconnect();
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.initialized = false;
    }

    initializeOptionSelection() {
      if (this.confirmedOptions) return;
      this.confirmedOptions = new Set();
      const variantId = new URL(window.location.href).searchParams.get('variant');
      const linkedVariant = this.config.variants.find(variant => String(variant.id) === variantId);
      if (linkedVariant) {
        this.variant = linkedVariant;
        this.syncRadios();
        this.confirmAllOptions();
      }
    }

    confirmOption(radio) {
      const position = Number(radio.dataset.optionPosition);
      this.confirmedOptions.add(position);
      this.fields.find(field => Number(field.dataset.optionIndex) === position)
        ?.querySelector('[data-selection-message]')?.setAttribute('hidden', '');
    }

    confirmAllOptions() {
      this.fields.forEach(field => {
        this.confirmedOptions.add(Number(field.dataset.optionIndex));
        field.querySelector('[data-selection-message]')?.setAttribute('hidden', '');
      });
    }

    getUnconfirmedOption() {
      if (!this.mobileQuery.matches) return;
      return this.fields.find(field => {
        const values = new Set([...field.querySelectorAll('[data-option-radio]')].map(radio => radio.value));
        return values.size > 1 && !this.confirmedOptions.has(Number(field.dataset.optionIndex));
      });
    }

    requestOptionSelection(field) {
      let message = field.querySelector('[data-selection-message]');
      if (!message) {
        message = document.createElement('p');
        message.className = 'er-product__selection-message er-mobile-only';
        message.id = `${this.form.getAttribute('id')}-choose-option-${field.dataset.optionIndex}`;
        message.setAttribute('data-selection-message', '');
        message.setAttribute('role', 'alert');
        field.append(message);
        field.querySelectorAll('[data-option-radio]').forEach(radio => {
          const describedBy = [radio.getAttribute('aria-describedby'), message.id].filter(Boolean).join(' ');
          radio.setAttribute('aria-describedby', describedBy);
        });
      }
      message.hidden = false;
      message.textContent = field.classList.contains('er-product__option--size')
        ? this.config.strings.chooseSize
        : this.config.strings.chooseOptions;
      const radio = field.querySelector('[data-option-radio]:checked') || field.querySelector('[data-option-radio]');
      radio?.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    }

    updateStickyVisibility() {
      const sticky = this.querySelector('[data-sticky-cart]');
      const rect = this.mainButton.getBoundingClientRect();
      const overlayOpen = [...document.querySelectorAll('dialog[open], header-drawer details[open], [aria-modal="true"]')]
        .some(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0);
      const hidden = !this.mobileQuery.matches || rect.height <= 0 || rect.width <= 0 || rect.bottom > 0 || overlayOpen;
      if (sticky.hidden !== hidden) sticky.hidden = hidden;
    }

    selectVariant(changedRadio) {
      this.confirmOption(changedRadio);
      const selected = [];
      this.fields.forEach(field => { selected[Number(field.dataset.optionIndex)] = field.querySelector('input:checked')?.value; });
      this.variant = this.config.variants.find(variant => variant.options.every((option, index) => option === selected[index]));
      this.querySelector('[data-cart-feedback]').hidden = true;
      this.renderVariant();
    }

    syncRadios() {
      if (!this.variant) return;
      this.radios.forEach(radio => { radio.checked = this.variant.options[Number(radio.dataset.optionPosition)] === radio.value; });
    }

    renderVariant(updateURL = true) {
      const variant = this.variant;
      const strings = this.config.strings;
      const stockNotice = this.querySelector('[data-stock]');
      if (stockNotice) stockNotice.hidden = !variant?.lowStock;
      const quantity = this.querySelector('[data-quantity]');
      if (quantity && !this.busy && quantity.dataset.variantId !== String(variant?.id || '')) {
        const min = variant?.quantityMin || 1;
        const step = variant?.quantityStep || 1;
        const max = variant?.quantityMax;
        quantity.min = String(min);
        quantity.step = String(step);
        quantity.value = String(min);
        if (max != null) quantity.max = String(max); else quantity.removeAttribute('max');
        this.querySelector('[data-quantity-wrap]').hidden = min === 1 && step === 1 && max == null;
        quantity.dataset.variantId = String(variant?.id || '');
      }
      const selectedOptions = [];
      this.fields.forEach(field => { selectedOptions[Number(field.dataset.optionIndex)] = field.querySelector('input:checked')?.value; });
      this.select.value = variant ? String(variant.id) : '';
      const available = !!variant?.available;
      this.querySelectorAll('[data-main-add], [data-sticky-add]').forEach(button => { button.disabled = !available || this.busy; });
      this.querySelectorAll('[data-add-text]').forEach(element => { element.textContent = this.busy ? strings.adding : available ? strings.add : variant ? strings.soldOut : strings.unavailable; });
      const mobileLabel = this.busy ? strings.adding : available ? (strings.addToBag || strings.add) : variant ? strings.soldOut : strings.unavailable;
      this.querySelectorAll('[data-mobile-add-text]').forEach(element => { element.textContent = mobileLabel; });
      this.fields.forEach(field => {
        const selectedText = selectedOptions[Number(field.dataset.optionIndex)] || '';
        field.querySelector('[data-selected-option]').textContent = field.classList.contains('er-product__option--size') ? selectedText.split('(')[0].trim() : selectedText;
      });
      this.radios.forEach(radio => {
        const position = Number(radio.dataset.optionPosition);
        const choices = this.config.variants.filter(candidate => candidate.options.every((option, index) => index === position ? option === radio.value : option === selectedOptions[index]));
        const availableChoice = choices.some(candidate => candidate.available);
        radio.closest('label').classList.toggle('is-unavailable', !availableChoice);
        radio.setAttribute('aria-disabled', String(!availableChoice));
        radio.setAttribute('aria-label', radio.value + (availableChoice ? '' : ' — ' + strings.soldOut));
      });
      const sale = variant && variant.compareAtPrice > variant.price;
      const percent = sale ? Math.round((variant.compareAtPrice - variant.price) / variant.compareAtPrice * 100) : 0;
      this.querySelector('[data-price-text]').textContent = variant?.priceText || '';
      this.querySelector('[data-sticky-price]').textContent = variant?.priceText || '';
      this.querySelector('[data-price]').classList.toggle('er-product__price--sale', !!sale);
      this.querySelector('[data-compare-text]').textContent = sale ? variant.compareText : '';
      ['[data-compare]', '[data-sale-badge]'].forEach(selector => { this.querySelector(selector).hidden = !sale; });
      this.querySelector('[data-sale-badge]').textContent = strings.save.replace('[percent]', percent);
      this.syncGallery(selectedOptions);
      if (updateURL) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
    }

    syncGallery(selectedOptions) {
      const slides = [...this.querySelectorAll('[data-media-id]')];
      const allIds = slides.map(slide => slide.dataset.mediaId);
      const colour = this.variant?.options[this.config.colourOptionIndex] || selectedOptions[this.config.colourOptionIndex];
      const group = this.config.mediaGroups?.find(item => item.colour === colour);
      const groupedIds = group?.imageIds.map(String).filter(id => allIds.includes(id)) || [];
      const eligibleIds = groupedIds.length ? groupedIds : allIds;
      this.visibleMediaIds = new Set(eligibleIds);
      const colourChanged = this.galleryColour !== colour;
      const currentId = slides.find(slide => !slide.hidden)?.dataset.mediaId;
      this.querySelectorAll('[data-thumb-id]').forEach(button => {
        button.hidden = !this.visibleMediaIds.has(button.dataset.thumbId);
      });
      const lookbookIds = new Set(groupedIds.length ? groupedIds : allIds.slice(0, 4));
      this.querySelectorAll('[data-lookbook-image]').forEach(link => {
        link.hidden = !lookbookIds.has(link.dataset.lookbookImage);
      });
      const lookbook = this.querySelector('.er-product__lookbook');
      if (lookbook) lookbook.hidden = lookbookIds.size < 2;
      const preferredId = colourChanged ? String(this.variant?.imageId || '') : currentId;
      const nextId = this.visibleMediaIds.has(preferredId) ? preferredId : eligibleIds[0];
      if (nextId) this.showImage(nextId);
      else slides.forEach(slide => { slide.hidden = true; });
      if (colourChanged) {
        const dialog = this.querySelector('[data-zoom-dialog]');
        if (dialog?.open) dialog.close();
        this.querySelector('[data-zoom-image-container]')?.replaceChildren();
        this.querySelectorAll('.er-product__thumbnails, .er-product__lookbook-grid').forEach(container => { container.scrollLeft = 0; });
      }
      this.galleryColour = colour;
    }

    showImage(id) {
      const target = this.querySelector(`[data-media-id="${id}"]`);
      if (!target || !this.visibleMediaIds?.has(String(id))) return;
      this.querySelectorAll('[data-media-id]').forEach(slide => { slide.hidden = slide !== target; });
      this.querySelectorAll('[data-thumb-id]').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.thumbId === String(id))); });
    }

    async addToCart(event) {
      const unconfirmedOption = this.getUnconfirmedOption();
      if (unconfirmedOption) {
        event.preventDefault();
        this.requestOptionSelection(unconfirmedOption);
        return;
      }
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      if (this.busy || !this.variant?.available) return;
      this.busy = true;
      this.renderVariant(false);
      const feedback = this.querySelector('[data-cart-feedback]');
      feedback.hidden = true;
      const formData = new FormData(this.form);
      this.querySelectorAll('[data-main-add], [data-sticky-add]').forEach(button => button.setAttribute('aria-busy', 'true'));
      const events = await this.cartEventsPromise;
      if (!events?.CartAddEvent) {
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }
      const { CartAddEvent } = events;
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
      if (sectionIds.length) {
        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
      }
      const itemCount = Number(formData.get('quantity')) || 1;
      let redirecting = false;
      let focusFeedback = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.config.strings.error);
        let cart;
        try {
          const cartResponse = await fetch(this.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          if (!cartResponse.ok) throw new Error(this.config.strings.networkError);
          cart = await cartResponse.json();
        } catch (error) {
          redirecting = true;
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        this.dispatchEvent(new CartAddEvent(cart, String(formData.get('id')), {
          source: 'product-form-component',
          itemCount,
          sections: data.sections,
          didError: false
        }));
        this.querySelector('[data-cart-message]').textContent = this.config.strings.added;
        this.querySelector('[data-view-cart]').hidden = false;
        feedback.dataset.error = 'false';
        focusFeedback = !document.querySelector('cart-drawer-component[auto-open]');
        this.updateCartCount(cart);
      } catch (error) {
        this.querySelector('[data-cart-message]').textContent = error instanceof TypeError ? this.config.strings.networkError : error.message || this.config.strings.error;
        this.querySelector('[data-view-cart]').hidden = false;
        feedback.dataset.error = 'true';
      } finally {
        if (redirecting) return;
        this.busy = false;
        this.querySelectorAll('[data-main-add], [data-sticky-add]').forEach(button => button.removeAttribute('aria-busy'));
        this.renderVariant(false);
        feedback.hidden = false;
        if (focusFeedback) {
          feedback.focus({ preventScroll: true });
          feedback.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        }
      }
    }

    updateCartCount(cart) {
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-er-cart-count]').forEach(element => { element.textContent = String(cart.item_count); });
      document.querySelectorAll('.cart-count-bubble').forEach(element => { element.hidden = cart.item_count === 0; });
      document.dispatchEvent(new CustomEvent('er:cart-updated', { detail: { cart } }));
    }
  });
}

